import React, { useEffect, useMemo, useState } from 'react';
import {
  FaExternalLinkAlt,
  FaLocationArrow,
  FaMapMarkerAlt,
  FaRedo,
  FaStore,
  FaTruck,
} from 'react-icons/fa';
import { loadDeliveryStores } from '../api/deliveryStoresApi';
import { DEFAULT_DELIVERY_CITY, SUPPORTED_DELIVERY_CITIES } from '../config/deliveryCities';
import '../styles/delivery-stores.css';

const PRICE_NOTICE =
  'Verfügbarkeit und aktuelle Preise siehst du direkt beim Anbieter.';

function normalizeCities(cities) {
  return Array.isArray(cities) && cities.length > 0 ? cities : SUPPORTED_DELIVERY_CITIES;
}

function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Standort ist auf diesem Gerät nicht verfügbar.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = Number(position?.coords?.latitude);
        const longitude = Number(position?.coords?.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          reject(new Error('Standort konnte nicht sauber gelesen werden.'));
          return;
        }

        resolve({
          latitude: Number(latitude.toFixed(6)),
          longitude: Number(longitude.toFixed(6)),
        });
      },
      () => reject(new Error('Standort wurde nicht freigegeben. Wähle einfach eine Stadt aus.')),
      { enableHighAccuracy: false, maximumAge: 180000, timeout: 9000 }
    );
  });
}

function StoreImage({ store }) {
  if (!store?.image_url) {
    return (
      <div className="pfp-delivery-store__image is-empty" aria-hidden="true">
        <FaStore />
      </div>
    );
  }

  return (
    <img
      className="pfp-delivery-store__image"
      src={store.image_url}
      alt=""
      loading="lazy"
      decoding="async"
    />
  );
}

function StoreCard({ store }) {
  const hasLink = Boolean(store?.affiliate_url);
  const categories = String(store?.categories || store?.raw_category || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 4);

  return (
    <article className="pfp-delivery-store">
      <StoreImage store={store} />
      <div className="pfp-delivery-store__body">
        <div className="pfp-delivery-store__topline">
          <span>Anzeige / Affiliate-Link</span>
          {store?.distance_km != null ? <strong>{store.distance_km} km</strong> : null}
        </div>
        <h2>{store?.store_name || 'Lieferando-Store'}</h2>
        {categories.length > 0 ? (
          <div className="pfp-delivery-store__tags" aria-label="Kategorien">
            {categories.map((category) => (
              <span key={category}>{category}</span>
            ))}
          </div>
        ) : null}
        <div className="pfp-delivery-store__facts">
          <span><FaMapMarkerAlt aria-hidden="true" /> {store?.city || 'Stadt offen'}</span>
          {store?.address || store?.postal_code ? (
            <span>{[store?.postal_code, store?.address].filter(Boolean).join(' ')}</span>
          ) : null}
          {store?.minimum_order_label ? (
            <span>Mindestwert ca. {store.minimum_order_label}</span>
          ) : null}
          {store?.delivery_cost_label ? (
            <span>Lieferkosten ca. {store.delivery_cost_label}</span>
          ) : null}
        </div>
        {store?.description ? <p>{store.description}</p> : null}
      </div>
      {hasLink ? (
        <a
          className="pfp-delivery-store__button"
          href={store.affiliate_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
        >
          Bei Lieferando ansehen <FaExternalLinkAlt aria-hidden="true" />
        </a>
      ) : null}
    </article>
  );
}

export default function DeliveryStoresPage() {
  const [city, setCity] = useState(DEFAULT_DELIVERY_CITY);
  const [cities, setCities] = useState(SUPPORTED_DELIVERY_CITIES);
  const [stores, setStores] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const [locationMessage, setLocationMessage] = useState('');

  const activeCityName = useMemo(() => {
    return normalizeCities(cities).find((item) => item.normalized === city)?.name || 'Dresden';
  }, [cities, city]);

  async function loadCityStores(nextCity = city) {
    setLoading(true);
    setError('');
    try {
      const response = await loadDeliveryStores({ city: nextCity, limit: 24 });
      const responseCities = normalizeCities(response?.supported_cities);
      setCities(responseCities);
      setStores(Array.isArray(response?.items) ? response.items : []);
      setMeta(response?.meta || null);
      setCity(response?.city_normalized || nextCity || DEFAULT_DELIVERY_CITY);
    } catch (requestError) {
      setStores([]);
      setError(requestError?.message || 'Stores konnten gerade nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCityStores(DEFAULT_DELIVERY_CITY);
  }, []);

  async function handleCityChange(event) {
    const nextCity = event.target.value || DEFAULT_DELIVERY_CITY;
    setCity(nextCity);
    setLocationMessage('');
    await loadCityStores(nextCity);
  }

  async function handleUseLocation() {
    setLocating(true);
    setLoading(true);
    setError('');
    setLocationMessage('');

    try {
      const position = await getCurrentPosition();
      const response = await loadDeliveryStores({
        lat: position.latitude,
        lng: position.longitude,
        limit: 24,
      });
      const responseCities = normalizeCities(response?.supported_cities);
      setCities(responseCities);
      setStores(Array.isArray(response?.items) ? response.items : []);
      setMeta(response?.meta || null);
      setCity(response?.city_normalized || DEFAULT_DELIVERY_CITY);
      const distance = response?.meta?.user_city_distance_km;
      setLocationMessage(
        distance != null
          ? `Standort wurde der nächsten unterstützten Stadt zugeordnet: ${response?.city || 'Dresden'} (${distance} km bis Stadtzentrum).`
          : `Standort wurde zugeordnet: ${response?.city || 'Dresden'}.`
      );
    } catch (locationError) {
      setLocationMessage(locationError?.message || 'Standort wurde nicht freigegeben.');
      await loadCityStores(city || DEFAULT_DELIVERY_CITY);
    } finally {
      setLocating(false);
      setLoading(false);
    }
  }

  return (
    <main className="pfp-delivery-page">
      <section className="pfp-delivery-hero">
        <div>
          <span className="pfp-delivery-kicker"><FaTruck aria-hidden="true" /> Lieferando / Awin</span>
          <h1>Getränke & Snacks liefern lassen</h1>
          <p>
            Du brauchst Getränke oder Snacks für unterwegs? Hier findest du passende Lieferando-Stores in deiner Nähe.
            Die Links führen zu Lieferando/Awin. {PRICE_NOTICE}
          </p>
        </div>
      </section>

      <section className="pfp-delivery-controls" aria-label="Store-Suche">
        <button type="button" onClick={handleUseLocation} disabled={locating || loading}>
          <FaLocationArrow aria-hidden="true" />
          {locating ? 'Standort wird geprüft' : 'Standort verwenden'}
        </button>
        <label>
          <span>Stadt</span>
          <select value={city} onChange={handleCityChange} disabled={loading || locating}>
            {normalizeCities(cities).map((item) => (
              <option key={item.normalized} value={item.normalized}>{item.name}</option>
            ))}
          </select>
        </label>
        <button type="button" className="is-secondary" onClick={() => loadCityStores(city)} disabled={loading || locating}>
          <FaRedo aria-hidden="true" />
          Aktualisieren
        </button>
      </section>

      <section className="pfp-delivery-status" aria-live="polite">
        <strong>{activeCityName}</strong>
        <span>Sortiert nach Relevanz. Der Feed enthält keine Live-Produktverfügbarkeit und keine Store-Koordinaten.</span>
        {locationMessage ? <em>{locationMessage}</em> : null}
      </section>

      {error ? (
        <section className="pfp-delivery-empty">
          <h2>Gerade keine Stores geladen</h2>
          <p>{error}</p>
          <button type="button" onClick={() => loadCityStores(city)}>Nochmal versuchen</button>
        </section>
      ) : null}

      {!error && loading ? (
        <section className="pfp-delivery-grid" aria-busy="true">
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="pfp-delivery-skeleton" key={index} />
          ))}
        </section>
      ) : null}

      {!error && !loading && stores.length === 0 ? (
        <section className="pfp-delivery-empty">
          <h2>Keine passenden Stores gefunden</h2>
          <p>Wähle eine andere Stadt oder versuche es später erneut.</p>
        </section>
      ) : null}

      {!error && !loading && stores.length > 0 ? (
        <section className="pfp-delivery-grid">
          {stores.map((store) => (
            <StoreCard store={store} key={store.id} />
          ))}
        </section>
      ) : null}

      <aside className="pfp-delivery-disclosure">
        Die Stores stammen aus einem Lieferando/Awin-Storefeed. Suchpreisangaben werden nur als Mindestwert ca. angezeigt.
        Verfügbarkeit, Liefergebiet und aktuelle Preise prüfst du direkt beim Anbieter.
        {meta?.source ? <span>Datenquelle: {meta.source === 'custom_table' ? 'normalisierte Store-Tabelle' : 'normalisierte Seed-Datei'}.</span> : null}
      </aside>
    </main>
  );
}
